import React, { Fragment, useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
  Box, Typography, Paper, InputLabel, Select, MenuItem, CircularProgress, Alert 
} from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import { format, formatDistanceToNow } from '../../utils/datetime';
import { fetchRequest as editWorkspace, clearState as clearEditState } from '../../redux/actions/workspaces/edit';
import { fetchRequest as fetchWorkspaces } from '../../redux/actions/workspaces/list';
import { fetchRequest as getUser } from '../../redux/actions/user/getUser';
import { 
  CMButton, CMButtonSuccess, CMFormControl, CMButtonOutlinedSmall, 
  CMSmallTextField, CMSmallChipsField 
} from '../../containers/styledComponents';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';

const safeFormatDate = (d, fmt = 'PPpp') => {
    try { return d ? format(new Date(d), fmt) : '—'; } catch (_e) { return '—'; }
};

// "in 6 days" / "in 3 hours"; "now" once it has passed.
const safeTimeUntil = (d) => {
    try {
        if (!d) return '';
        const t = new Date(d);
        if (t.getTime() <= Date.now()) return 'now';
        return `in ${formatDistanceToNow(t)}`;
    } catch (_e) { return ''; }
};

const EditWorkspacePage = ({ editWorkspaceAction, editLoading, editPayload, editError, clearEditStateAction, workspaces, workspacesLoading, fetchWorkspacesAction, getUser }) => {
    const navigate = useNavigate();
    const { workspaceId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { isDarkMode } = useCustomTheme();

    const [workspaceData, setWorkspaceData] = useState({
        name: "",
        description: "",
        purpose: "",
        keywords: [],
        sectors: [],
        data_granularity: "day"
    });

    // Ephemeral workspaces carry an expiry; "Keep" clears it.
    const [expiresAt, setExpiresAt] = useState(null);
    const [keeping, setKeeping] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [workspaceNotFound, setWorkspaceNotFound] = useState(false);

    // Fetch workspaces if not already loaded
    useEffect(() => {
        if (!workspaces || !workspaces.workspaces) {
            fetchWorkspacesAction();
        }
    }, [workspaces, fetchWorkspacesAction]);

    // Find and load the workspace data
    useEffect(() => {
        if (workspaces && workspaces.workspaces && workspaceId) {
            const workspace = workspaces.workspaces.find(p => p.uid === workspaceId);
            if (workspace) {
                setWorkspaceData({
                    name: workspace.name || "",
                    description: workspace.description || "",
                    purpose: workspace.purpose || "",
                    keywords: workspace.keywords || [],
                    sectors: workspace.sectors || [],
                    data_granularity: workspace.data_granularity || "day"
                });
                setExpiresAt(workspace.expires_at || null);
                setIsLoading(false);
                setWorkspaceNotFound(false);
            } else if (!workspacesLoading) {
                setWorkspaceNotFound(true);
                setIsLoading(false);
            }
        }
    }, [workspaces, workspaceId, workspacesLoading]);

    // Handle edit success/error
    useEffect(() => {
        if (editPayload) {
            enqueueSnackbar(
                keeping ? 'Workspace kept - it is now permanent' : 'Workspace updated successfully',
                { variant: 'success' },
            );
            clearEditStateAction();
            getUser();
            fetchWorkspacesAction();
            navigate('/workspaces');
        }
        if (editError) {
            enqueueSnackbar('Error updating workspace', { variant: 'error' });
            setKeeping(false);
        }
    }, [editPayload, editError, clearEditStateAction, navigate, enqueueSnackbar, getUser, fetchWorkspacesAction, keeping]);

    // Clear the expiry: the workspace becomes permanent.
    const handleKeep = () => {
        setKeeping(true);
        editWorkspaceAction(workspaceId, { expires_at: null });
    };

    const handleChangeSectors = (newChips) => {
        setWorkspaceData({
            ...workspaceData,
            sectors: newChips
        });
    };
    
    const handleChangeKeywords = (newChips) => {
        setWorkspaceData({
            ...workspaceData,
            keywords: newChips
        });
    };

    const handleInputChange = (field, value) => {
        setWorkspaceData({
            ...workspaceData,
            [field]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Basic validation
        if (!workspaceData.name.trim()) {
            enqueueSnackbar('Workspace name is required', { variant: 'error' });
            return;
        }

        // Call edit API
        editWorkspaceAction(workspaceId, {
            name: workspaceData.name.trim(),
            description: workspaceData.description.trim(),
            purpose: workspaceData.purpose.trim(),
            keywords: workspaceData.keywords,
            sectors: workspaceData.sectors,
            data_granularity: workspaceData.data_granularity
        });
    };

    const handleBack = () => {
        navigate('/workspaces');
    };

    // Show loading state
    if (isLoading || workspacesLoading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                gap: 2
            }}>
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                    Loading workspace details...
                </Typography>
            </Box>
        );
    }

    // Show workspace not found
    if (workspaceNotFound) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '100vh',
                pt: 4,
                px: 3
            }}>
                <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
                    <Typography variant="h6">Workspace not found</Typography>
                    <Typography variant="body2">
                        The workspace you're trying to edit could not be found or you don't have permission to access it.
                    </Typography>
                </Alert>
                <CMButtonOutlinedSmall
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Back to workspaces
                </CMButtonOutlinedSmall>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '100vh',
            pt: 4,
            px: 3
        }}>
            <PageBreadcrumbs 
                items={[
                    { label: 'Workspaces', path: '/workspaces' },
                    { label: 'Edit workspace', path: `/workspace/${workspaceId}/edit` }
                ]}
            />

            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                width: '80%',
                minWidth: '500px'
            }}>
                <DnsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                    Edit workspace
                </Typography>
                <CMButtonOutlinedSmall
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Back
                </CMButtonOutlinedSmall>
            </Box>

            {expiresAt && (
                <Alert
                    severity="warning"
                    icon={<HourglassBottomIcon fontSize="inherit" />}
                    sx={{ mb: 3, width: '80%', minWidth: '500px', alignItems: 'center' }}
                    action={
                        <CMButton
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<BookmarkAddedIcon />}
                            onClick={handleKeep}
                            disabled={editLoading}
                        >
                            {keeping && editLoading ? 'Keeping…' : 'Keep this workspace'}
                        </CMButton>
                    }
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Ephemeral workspace · expires {safeTimeUntil(expiresAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        On {safeFormatDate(expiresAt)} this workspace and everything in it is deleted.
                        Keep it to make it permanent.
                    </Typography>
                </Alert>
            )}

            <Paper sx={{ 
                p: 3, 
                mb: 4,
                width: '80%',
                minWidth: '500px'
            }}>
                <form onSubmit={handleSubmit}>
                    <Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <EditIcon sx={{ mr: 1 }} />
                                Workspace details
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Workspace name *
                                </Typography>
                                <CMSmallTextField
                                    value={workspaceData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Enter workspace name"
                                    required
                                    fullWidth
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Description
                                </Typography>
                                <CMSmallTextField
                                    value={workspaceData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Describe your workspace"
                                    multiline
                                    rows={4}
                                    fullWidth
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Goal
                                </Typography>
                                <CMSmallTextField
                                    value={workspaceData.purpose}
                                    onChange={(e) => handleInputChange('purpose', e.target.value)}
                                    placeholder="What are you trying to predict or achieve in this workspace?"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Keywords
                                </Typography>
                                <CMSmallChipsField
                                    chips={workspaceData.keywords}
                                    onChange={handleChangeKeywords}
                                    placeholder="Add keywords (press Enter to add)"
                                    fullWidth
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Sectors
                                </Typography>
                                <CMSmallChipsField
                                    chips={workspaceData.sectors}
                                    onChange={handleChangeSectors}
                                    placeholder="Add sectors (press Enter to add)"
                                    fullWidth
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                    Data Granularity
                                </Typography>
                                <CMFormControl fullWidth>
                                    <Select
                                        value={workspaceData.data_granularity}
                                        onChange={(e) => handleInputChange('data_granularity', e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="second">Second</MenuItem>
                                        <MenuItem value="minute">Minute</MenuItem>
                                        <MenuItem value="hour">Hour</MenuItem>
                                        <MenuItem value="day">Day</MenuItem>
                                        <MenuItem value="week">Week</MenuItem>
                                        <MenuItem value="month">Month</MenuItem>
                                        <MenuItem value="year">Year</MenuItem>
                                    </Select>
                                </CMFormControl>
                            </Box>

                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <CMButtonOutlinedSmall
                                    onClick={handleBack}
                                    disabled={editLoading}
                                >
                                    Cancel
                                </CMButtonOutlinedSmall>
                                <CMButton
                                    startIcon={<SaveIcon />}
                                    type="submit" 
                                    variant="contained" 
                                    color="primary"
                                    disabled={editLoading}
                                >
                                    {editLoading ? 'Updating...' : 'Update workspace'}
                                </CMButton>
                            </Box>
                        </Box>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

const mapStateToProps = (state) => {
    return {
        editLoading: state.workspace_edit.isLoading,
        editPayload: state.workspace_edit.response,
        editError: state.workspace_edit.error,
        workspaces: state.workspace_list.response,
        workspacesLoading: state.workspace_list.isLoading,
    };
};

const mapDispatchToProps = (dispatch) => ({
    editWorkspaceAction: (workspaceId, data) => dispatch(editWorkspace(workspaceId, data)),
    clearEditStateAction: () => dispatch(clearEditState()),
    fetchWorkspacesAction: () => dispatch(fetchWorkspaces({})),
    getUser: () => dispatch(getUser()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditWorkspacePage);
